import {useSigma} from "@react-sigma/core";
import {useEffect, useMemo, useRef} from "react";
import {useSubNetworkContext} from "../../contexts/SubNetworkContext";
import {useAnnotationMap} from "../../hooks/useAnnotationMap";
import {UNANNOTATED_TERM_NAME} from "../../types/consts";

// Controller component: maps legend hover state to Sigma node highlight attributes
const SubNetworkLegendHighlightController = () => {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const annotationMap = useAnnotationMap();
  const {displayedTerms, legendHoveredTerm} = useSubNetworkContext();
  // Tracks currently highlighted nodes without triggering React re-renders
  const highlightedNodesRef = useRef<Set<string>>(new Set());

  // Resolve hovered legend term against displayed terms to avoid stale hover state
  const activeTerm = useMemo(() => {
    if (!legendHoveredTerm) return undefined;
    return displayedTerms.find(term => term.name === legendHoveredTerm.name);
  }, [displayedTerms, legendHoveredTerm]);

  // Diff previous vs next highlight state and update graph attributes minimally
  useEffect(() => {
    if (!graph) return;

    const nextHighlighted = new Set<string>();

    if (activeTerm) {
      const targetName = activeTerm.name;
      graph.forEachNode((node) => {
        // Match annotated nodes or unannotated nodes when hovering the "Unannotated" legend entry
        const nodeId = Number(node);
        if (!Number.isFinite(nodeId)) return;

        const nodeAnnotation = annotationMap.get(nodeId);
        const isAnnotatedMatch = nodeAnnotation?.name === targetName;
        const isUnannotatedMatch = !nodeAnnotation && targetName === UNANNOTATED_TERM_NAME;

        if (isAnnotatedMatch || isUnannotatedMatch) {
          nextHighlighted.add(node);
        }
      });
    }

    const prevHighlighted = highlightedNodesRef.current;

    // Remove highlight attributes from nodes no longer matching the active term
    prevHighlighted.forEach((node) => {
      if (!nextHighlighted.has(node) && graph.hasNode(node)) {
        graph.setNodeAttribute(node, "highlighted", false);
        graph.setNodeAttribute(node, "legendHighlighted", false);
      }
    });

    // Apply highlight attributes only to newly matched nodes
    nextHighlighted.forEach((node) => {
      if (!prevHighlighted.has(node) && graph.hasNode(node)) {
        graph.setNodeAttribute(node, "highlighted", true);
        graph.setNodeAttribute(node, "legendHighlighted", true);
      }
    });

    highlightedNodesRef.current = nextHighlighted;
  }, [activeTerm, annotationMap, graph]);

  // Clear any remaining legend-driven highlights on unmount
  useEffect(() => () => {
    if (!graph) return;

    highlightedNodesRef.current.forEach((node) => {
      if (graph.hasNode(node)) {
        graph.setNodeAttribute(node, "highlighted", false);
        graph.setNodeAttribute(node, "legendHighlighted", false);
      }
    });
    highlightedNodesRef.current.clear();
  }, [graph]);

  return null;
};

export default SubNetworkLegendHighlightController;
